import { parseNumberConfig } from '~/utils/configUtils';

const ITEM_KEYS = {
  /** お知らせカテゴリの色 */
  NOTICE_CATEGORY_COLOR: 'notice-category-color',
  /** お知らせカテゴリの名称 */
  NOTICE_CATEGORY_NAME: 'notice-category-name',
  /** 作業種別の色 */
  WORK_TYPE_COLOR: 'work-type-color',
  /** 速度監視設定 */
  SPEED_MONITORING_SETTINGS: {
    KEY: 'speed-monitoring-settings',
    CODES: {
      INTERVAL: 'interval',
      ACCURACY_THRESHOLD: 'accuracy-threshold',
      EXCEEDED_SPEED_THRESHOLD: 'exceeded-speed-threshold',
      STOP_SPEED_THRESHOLD: 'stop-speed-threshold',
      EXCEEDED_COUNT_THRESHOLD: 'exceeded-count-threshold',
      STOP_COUNT_THRESHOLD: 'stop-count-threshold',
      GPS_FAILED_COUNT_THRESHOLD: 'gps-failed-count-threshold',
      WARNING_SUPPRESS_DURATION_SECONDS: 'warning-suppress-duration-seconds',
    },
  },
  /** 駅エリア検知設定 */
  IN_STATION_DETECTION_SETTINGS: {
    KEY: 'in-station-detection-settings',
    CODES: {
      CHECK_INTERVAL: 'check-interval',
      STAY_THRESHOLD: 'stay-threshold',
      MAX_ACCURACY: 'max-accuracy',
    },
  },
  /** GPS取得設定 */
  GPS_ACQUISITION_SETTINGS: {
    KEY: 'gps-acquisition-settings',
    CODES: {
      WATCH_TIMEOUT: 'watch-timeout',
      ONE_SHOT_TIMEOUT: 'one-shot-timeout',
    },
  },
  /** よく使う駅設定 */
  FREQUENT_STATION_SETTINGS: {
    KEY: 'frequent-station-settings',
    CODES: {
      DISPLAY_COUNT: 'display-count',
    },
  },
} as const;

/**
 * 駅からのお知らせカテゴリ情報
 */
export type NoticeCategoryInfo = {
  code: string;
  name: string;
  color: string;
};

/**
 * 作業種別の色設定
 */
export type WorkTypeColorInfo = {
  workType: string;
  workTypeDetailKbn: string;
  color: string;
};

/**
 * 速度監視設定
 */
export type SpeedMonitoringSettings = {
  /** 速度監視の間隔（ミリ秒） */
  interval: number;
  /** 取得位置の最大許容誤差（メートル） */
  accuracyThreshold: number;
  /** 速度超過と判定する速度の上限閾値（km/h） */
  exceededSpeedThreshold: number;
  /** 停止（速度超過解除）と判定する速度の下限閾値（km/h） */
  stopSpeedThreshold: number;
  /** 速度超過を確定するまでの連続判定回数 */
  exceededCountThreshold: number;
  /** 停止（速度超過解除）を確定するまでの連続判定回数 */
  stopCountThreshold: number;
  /** GPS取得失敗回数の閾値 */
  gpsFailedCountThreshold: number;
  /** 警告メッセージを非表示にする時間（秒） */
  warningSuppressDurationSeconds: number;
};

/**
 * 駅エリア検知設定
 */
export type InStationDetectionSettings = {
  /** 計測間隔（ミリ秒） */
  checkInterval: number;
  /** 滞在判定閾値（回） */
  stayThreshold: number;
  /** エリア検知の最大許容制度（メートル） */
  maxAccuracy: number;
};

/**
 * GPS取得設定
 */
export type GpsAcquisitionSettings = {
  /** 継続測位（watchPosition）のタイムアウト時間（ミリ秒） */
  watchTimeout: number;
  /** 一回取得（getCurrentPosition）のタイムアウト時間（ミリ秒） */
  oneShotTimeout: number;
};

/**
 * よく使う駅設定
 */
export type FrequentStationSettings = {
  /** よく使う駅の最大表示件数（件） */
  displayCount: number;
};

export const useSystemConfig = (displayId: string) => {
  const { getSystemConfig } = useMasterStore();

  /**
   * お知らせカテゴリ情報を取得する
   */
  const getNoticeCategories = async (): Promise<NoticeCategoryInfo[]> => {
    const noticeCategoryColors = await getSystemConfig(displayId, ITEM_KEYS.NOTICE_CATEGORY_COLOR);
    const noticeCategoryNames = await getSystemConfig(displayId, ITEM_KEYS.NOTICE_CATEGORY_NAME);

    // システム設定情報から取得した色情報と名称情報をマージして返却
    const colorsMap = new Map(noticeCategoryColors.map((ncc) => [ncc.itemCd, ncc.itemValue]));
    return noticeCategoryNames.map((ncn) => {
      const color = colorsMap.get(ncn.itemCd) ?? '#FFF';
      return {
        code: ncn.itemCd,
        name: ncn.itemValue,
        color,
      };
    });
  };

  /**
   * 作業種別の色情報を取得する
   */
  const getWorkTypeColors = async () => {
    const workTypeColors = await getSystemConfig(displayId, ITEM_KEYS.WORK_TYPE_COLOR);
    return workTypeColors.map((wtc) => ({
      workType: wtc.itemCd.substring(0, 1),
      workTypeDetailKbn: wtc.itemCd.substring(1, 2),
      color: wtc.itemValue,
    }));
  };

  /**
   * 速度監視設定を取得する
   */
  const getSpeedMonitoringSettings = async (): Promise<SpeedMonitoringSettings> => {
    const speedMonitoringSettings = await getSystemConfig(displayId, ITEM_KEYS.SPEED_MONITORING_SETTINGS.KEY);

    let interval: string | undefined;
    let accuracyThreshold: string | undefined;
    let exceededSpeedThreshold: string | undefined;
    let stopSpeedThreshold: string | undefined;
    let exceededCountThreshold: string | undefined;
    let stopCountThreshold: string | undefined;
    let gpsFailedCountThreshold: string | undefined;
    let warningSuppressDurationSeconds: string | undefined;
    speedMonitoringSettings.forEach((s) => {
      switch (s.itemCd) {
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.INTERVAL:
          interval = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.ACCURACY_THRESHOLD:
          accuracyThreshold = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.EXCEEDED_SPEED_THRESHOLD:
          exceededSpeedThreshold = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.STOP_SPEED_THRESHOLD:
          stopSpeedThreshold = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.EXCEEDED_COUNT_THRESHOLD:
          exceededCountThreshold = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.STOP_COUNT_THRESHOLD:
          stopCountThreshold = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.GPS_FAILED_COUNT_THRESHOLD:
          gpsFailedCountThreshold = s.itemValue;
          break;
        case ITEM_KEYS.SPEED_MONITORING_SETTINGS.CODES.WARNING_SUPPRESS_DURATION_SECONDS:
          warningSuppressDurationSeconds = s.itemValue;
          break;
      }
    });

    return {
      interval: parseNumberConfig(interval, { defaultValue: 1000, min: 1 }),
      accuracyThreshold: parseNumberConfig(accuracyThreshold, { defaultValue: 100, min: 0 }),
      exceededSpeedThreshold: parseNumberConfig(exceededSpeedThreshold, { defaultValue: 10, min: 0 }),
      stopSpeedThreshold: parseNumberConfig(stopSpeedThreshold, { defaultValue: 7, min: 0 }),
      exceededCountThreshold: parseNumberConfig(exceededCountThreshold, { defaultValue: 3, min: 1 }),
      stopCountThreshold: parseNumberConfig(stopCountThreshold, { defaultValue: 3, min: 1 }),
      gpsFailedCountThreshold: parseNumberConfig(gpsFailedCountThreshold, { defaultValue: 5, min: 0 }),
      warningSuppressDurationSeconds: parseNumberConfig(warningSuppressDurationSeconds, { defaultValue: 60, min: 1 }),
    };
  };

  /**
   * 駅エリア検知設定を取得する
   */
  const getInStationDetectionSettings = async (): Promise<InStationDetectionSettings> => {
    const speedMonitoringSettings = await getSystemConfig(displayId, ITEM_KEYS.IN_STATION_DETECTION_SETTINGS.KEY);

    let checkInterval: string | undefined;
    let stayThreshold: string | undefined;
    let maxAccuracy: string | undefined;
    speedMonitoringSettings.forEach((s) => {
      switch (s.itemCd) {
        case ITEM_KEYS.IN_STATION_DETECTION_SETTINGS.CODES.CHECK_INTERVAL:
          checkInterval = s.itemValue;
          break;
        case ITEM_KEYS.IN_STATION_DETECTION_SETTINGS.CODES.STAY_THRESHOLD:
          stayThreshold = s.itemValue;
          break;
        case ITEM_KEYS.IN_STATION_DETECTION_SETTINGS.CODES.MAX_ACCURACY:
          maxAccuracy = s.itemValue;
          break;
      }
    });

    return {
      checkInterval: parseNumberConfig(checkInterval, { defaultValue: 1000, min: 1 }),
      stayThreshold: parseNumberConfig(stayThreshold, { defaultValue: 3, min: 1 }),
      maxAccuracy: parseNumberConfig(maxAccuracy, { defaultValue: 100, min: 0 }),
    };
  };

  /**
   * GPS取得設定を取得する
   */
  const getGpsAcquisitionSettings = async (): Promise<GpsAcquisitionSettings> => {
    const gpsAcquisitionSettings = await getSystemConfig(displayId, ITEM_KEYS.GPS_ACQUISITION_SETTINGS.KEY);

    let watchTimeout: string | undefined;
    let oneShotTimeout: string | undefined;
    gpsAcquisitionSettings.forEach((s) => {
      switch (s.itemCd) {
        case ITEM_KEYS.GPS_ACQUISITION_SETTINGS.CODES.WATCH_TIMEOUT:
          watchTimeout = s.itemValue;
          break;
        case ITEM_KEYS.GPS_ACQUISITION_SETTINGS.CODES.ONE_SHOT_TIMEOUT:
          oneShotTimeout = s.itemValue;
          break;
      }
    });

    return {
      watchTimeout: parseNumberConfig(watchTimeout, { defaultValue: 10000, min: 1 }),
      oneShotTimeout: parseNumberConfig(oneShotTimeout, { defaultValue: 30000, min: 1 }),
    };
  };

  /**
   * よく使う駅設定を取得する
   */
  const getFrequentStationSettings = async (): Promise<FrequentStationSettings> => {
    const frequentStationSettings = await getSystemConfig(displayId, ITEM_KEYS.FREQUENT_STATION_SETTINGS.KEY);

    const displayCount = frequentStationSettings.find(
      (s) => s.itemCd === ITEM_KEYS.FREQUENT_STATION_SETTINGS.CODES.DISPLAY_COUNT,
    )?.itemValue;

    return {
      displayCount: parseNumberConfig(displayCount, { defaultValue: 10, min: 1 }),
    };
  };

  return {
    getNoticeCategories,
    getWorkTypeColors,
    getSpeedMonitoringSettings,
    getInStationDetectionSettings,
    getGpsAcquisitionSettings,
    getFrequentStationSettings,
  };
};
